import { useState, useMemo, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, Power, Copy, ChevronRight, Code2, FlaskConical, Info } from "lucide-react";

const card = "rounded-2xl border border-sky/70 bg-white p-4";
const clone = (x) => JSON.parse(JSON.stringify(x));

const METHOD_BADGE = {
  weighted: "Weighted", scale: "Scale", benchmark_load: "Benchmark load",
  percentage: "Percentage", inverse_pct: "Inverse %", boolean: "Boolean", identity: "Direct",
};

function readableFormula(components) {
  return (components || []).filter((c) => c.enabled !== false)
    .map((c) => `${c.weight}% ${c.label}`).join("  +  ");
}

function ScaleTable({ scale }) {
  const bands = scale?.bands || [];
  return (
    <table className="mt-2 w-full overflow-hidden rounded-lg border border-sky/50 text-xs">
      <tbody className="divide-y divide-sky/30">
        {bands.map((b, i) => (
          <tr key={i} className="bg-mist/20">
            <td className="px-2 py-1 text-ink/60">{b.label || (b.max != null ? `≤ ${b.max}` : b.min != null ? `≥ ${b.min}` : "—")}</td>
            <td className="px-2 py-1 text-right font-semibold text-navy">{b.score}</td>
          </tr>
        ))}
        {scale?.empty_label != null && (
          <tr className="bg-red-50/40"><td className="px-2 py-1 text-ink/50">{scale.empty_label}</td><td className="px-2 py-1 text-right font-semibold text-red-600">{scale.empty_score}</td></tr>
        )}
      </tbody>
    </table>
  );
}

function MethodDetail({ comp, onBenchmark }) {
  if (comp.method === "scale" && comp.scale) return <ScaleTable scale={comp.scale} />;
  if (comp.method === "benchmark_load")
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-ink/60">
        <code className="rounded bg-mist px-1.5 py-0.5">min(100, {comp.metric} / benchmark × 100)</code>
        <span>benchmark</span>
        <input type="number" data-testid={`benchmark-${comp.key}`} className="h-7 w-20 rounded-lg border border-sky px-2 text-xs" value={comp.benchmark ?? 0} onChange={(e) => onBenchmark(Number(e.target.value))} />
      </div>
    );
  if (comp.method === "inverse_pct")
    return <div className="mt-1.5 text-xs text-ink/50"><code className="rounded bg-mist px-1.5 py-0.5">{comp.formula || `100 − ${comp.metric}`}</code></div>;
  if (comp.method === "boolean")
    return <div className="mt-1.5 text-xs text-ink/50">true → {comp.true_score ?? 100} · false → {comp.false_score ?? 0}</div>;
  if (comp.method === "identity" || comp.method === "percentage")
    return <div className="mt-1.5 text-xs text-ink/50"><code className="rounded bg-mist px-1.5 py-0.5">{comp.formula || comp.metric}</code></div>;
  return null;
}

function ComponentCard({ comp, path, setWeight, setBenchmark, toggle, isSub }) {
  const subs = comp.components || [];
  return (
    <div data-testid={`comp-${comp.key}`} className={`rounded-xl border p-3 ${comp.enabled === false ? "border-ink/10 bg-ink/5 opacity-60" : isSub ? "border-sky/40 bg-white" : "border-sky/60 bg-mist/20"}`}>
      <div className="flex items-center gap-2">
        <span className="font-sora text-sm font-semibold text-navy">{comp.label}</span>
        <span className="rounded-md border border-sky bg-white px-1.5 py-0.5 text-[10px] font-semibold text-ink/50">{METHOD_BADGE[comp.method] || comp.method}</span>
        {comp.neutral_score != null && <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700" title={comp.applies_when}>neutral {comp.neutral_score} if N/A</span>}
        <div className="ml-auto flex items-center gap-1.5">
          <input type="number" data-testid={`weight-${comp.key}`} className="h-8 w-16 rounded-lg border border-sky px-2 text-sm font-semibold text-navy" value={comp.weight ?? 0} onChange={(e) => setWeight(path, Number(e.target.value))} />
          <span className="text-xs text-ink/50">%</span>
          <button onClick={() => toggle(path)} title="Enable / disable" className={`rounded-lg p-1 ${comp.enabled === false ? "text-ink/30" : "text-loop"} hover:bg-loop/5`}><Power className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {comp.description && <p className="mt-1 text-[11px] text-ink/45">{comp.description}</p>}
      <MethodDetail comp={comp} onBenchmark={(v) => setBenchmark(path, v)} />
      {subs.length > 0 && (
        <div className="mt-2 space-y-1.5 border-l-2 border-loop/20 pl-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ink/40">Sub-components ({readableFormula(subs)})</p>
          {subs.map((sc, i) => (
            <div key={sc.key} data-testid={`comp-${sc.key}`} className={`rounded-lg border p-2.5 ${sc.enabled === false ? "border-ink/10 bg-ink/5 opacity-60" : "border-sky/40 bg-white"}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-navy">{sc.label}</span>
                <span className="rounded-md border border-sky bg-white px-1.5 py-0.5 text-[10px] font-semibold text-ink/50">{METHOD_BADGE[sc.method] || sc.method}</span>
                <div className="ml-auto flex items-center gap-1.5">
                  <input type="number" data-testid={`weight-${sc.key}`} className="h-7 w-14 rounded-lg border border-sky px-2 text-xs font-semibold text-navy" value={sc.weight ?? 0} onChange={(e) => setWeight([...path, i], Number(e.target.value))} />
                  <span className="text-xs text-ink/50">%</span>
                  <button onClick={() => toggle([...path, i])} className={`rounded-lg p-1 ${sc.enabled === false ? "text-ink/30" : "text-loop"} hover:bg-loop/5`}><Power className="h-3 w-3" /></button>
                </div>
              </div>
              <MethodDetail comp={sc} onBenchmark={(v) => setBenchmark([...path, i], v)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score, label, insufficient }) {
  const tone = insufficient ? "bg-ink/10 text-ink/50" : score >= 70 ? "bg-emerald-100 text-emerald-700" : score >= 55 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return (
    <div className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 ${tone}`} data-testid="test-score-badge">
      <span className="font-sora text-2xl font-bold">{insufficient ? "—" : score}</span>
      <span className="text-sm font-semibold">{label}</span>
    </div>
  );
}

function TestPanel({ entityType }) {
  const [entities, setEntities] = useState([]);
  const [sel, setSel] = useState("");
  const [range, setRange] = useState({ created_from: "", created_to: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadEntities = async () => {
    try { const { data } = await api.get(`/scoring/entities/${entityType}`); setEntities(data.data); if (data.data[0]) setSel(data.data[0].id); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadEntities(); /* eslint-disable-line */ }, [entityType]);

  const compute = async () => {
    if (!sel) { loadEntities(); return; }
    setLoading(true);
    try {
      const params = {};
      if (range.created_from) params.created_from = range.created_from;
      if (range.created_to) params.created_to = range.created_to;
      const { data } = await api.post("/scoring/compute", { entity_type: entityType, entity_id: sel, params });
      setResult(data.data);
    } catch (e) { toast.error(apiError(e)); setResult(null); }
    finally { setLoading(false); }
  };

  const sevTone = { critical: "text-red-700 bg-red-50", warning: "text-amber-700 bg-amber-50", positive: "text-emerald-700 bg-emerald-50", info: "text-sky-700 bg-sky-50" };

  return (
    <div className={card} data-testid="test-panel">
      <h3 className="mb-1 flex items-center gap-1.5 font-sora text-sm font-semibold text-navy"><FlaskConical className="h-4 w-4 text-loop" />Test Calculation</h3>
      <p className="mb-3 text-xs text-ink/50">Run this score against real data to verify your formula before trusting it.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div><label className="mb-1 block text-[11px] font-semibold uppercase text-ink/50">Entity</label>
          <select data-testid="test-entity-select" className="h-9 min-w-[180px] rounded-lg border border-sky px-2 text-sm" value={sel} onClick={() => !entities.length && loadEntities()} onChange={(e) => setSel(e.target.value)}>
            {entities.length === 0 && <option value="">Loading…</option>}
            {entities.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
        <div><label className="mb-1 block text-[11px] font-semibold uppercase text-ink/50">From</label>
          <input type="date" className="h-9 rounded-lg border border-sky px-2 text-sm" value={range.created_from} onChange={(e) => setRange((r) => ({ ...r, created_from: e.target.value }))} /></div>
        <div><label className="mb-1 block text-[11px] font-semibold uppercase text-ink/50">To</label>
          <input type="date" className="h-9 rounded-lg border border-sky px-2 text-sm" value={range.created_to} onChange={(e) => setRange((r) => ({ ...r, created_to: e.target.value }))} /></div>
        <button onClick={compute} disabled={loading} data-testid="test-compute-btn" className="flex h-9 items-center gap-1.5 rounded-lg bg-loop px-4 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}Calculate
        </button>
      </div>

      {result && (
        <div className="mt-4 space-y-3" data-testid="test-result">
          <div className="flex flex-wrap items-center gap-3">
            <ScoreBadge score={result.score} label={result.label} insufficient={result.insufficient} />
            <span className="text-xs text-ink/40">{result.entity_name} · {result.config_name} v{result.version}</span>
          </div>
          {!result.insufficient && (
            <div className="space-y-1.5">
              {result.breakdown.map((b) => (
                <div key={b.key} className="rounded-lg border border-sky/50 bg-mist/20 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-navy">{b.label}</span>
                    <span className="text-[11px] text-ink/40">{b.weight}%</span>
                    <div className="ml-auto flex items-center gap-2">
                      {b.available ? (
                        <><div className="h-1.5 w-24 overflow-hidden rounded-full bg-ink/10"><div className="h-full rounded-full bg-loop" style={{ width: `${b.score}%` }} /></div>
                          <span className="w-10 text-right font-semibold text-navy">{b.score}</span></>
                      ) : <span className="text-[11px] italic text-ink/40">no data</span>}
                    </div>
                  </div>
                  {b.sub && (
                    <div className="mt-1 space-y-0.5 pl-3">
                      {b.sub.map((s) => (
                        <div key={s.key} className="flex items-center gap-2 text-xs text-ink/55">
                          <span>{s.label}</span><span className="text-ink/30">{s.weight}%</span>
                          <span className="ml-auto font-semibold text-ink/60">{s.score ?? "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {result.flags?.length > 0 && (
            <div className="space-y-1">
              <p className="text-[11px] font-semibold uppercase text-ink/40">Diagnosis</p>
              {result.flags.map((f) => (
                <div key={f.key} className={`rounded-lg px-3 py-2 ${sevTone[f.severity] || "bg-sky-50 text-sky-700"}`}>
                  <span className="text-sm font-semibold">{f.label}</span>
                  {f.diagnosis && <p className="text-xs opacity-80">{f.diagnosis}</p>}
                  {f.recommended_action && <p className="text-xs">→ {f.recommended_action}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ScoringConfigEditor({ config, meta, onSaved }) {
  const [c, setC] = useState(() => clone(config));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [json, setJson] = useState("");

  const total = (c.components || []).filter((x) => x.enabled !== false).reduce((s, x) => s + (Number(x.weight) || 0), 0);
  const weightOk = Math.round(total) === 100;

  // path = array of indices into nested components
  const mutate = (path, fn) => setC((s) => {
    const n = clone(s); let arr = n.components;
    for (let i = 0; i < path.length - 1; i++) arr = arr[path[i]].components;
    fn(arr[path[path.length - 1]]);
    return n;
  });
  const setWeight = (path, v) => mutate(path, (node) => { node.weight = v; });
  const setBenchmark = (path, v) => mutate(path, (node) => { node.benchmark = v; });
  const toggle = (path) => mutate(path, (node) => { node.enabled = node.enabled === false; });
  const setLabel = (i, k, v) => setC((s) => { const n = clone(s); n.score_labels[i][k] = k === "min" ? Number(v) : v; return n; });

  const save = async () => {
    let payload;
    if (advanced) {
      try { payload = JSON.parse(json); } catch (e) { toast.error("Invalid JSON"); return; }
    } else {
      payload = {
        name: c.name, description: c.description || "", purpose: c.purpose || "",
        entity_type: c.entity_type, is_active: c.is_active,
        min_data_metric: c.min_data_metric || null, min_data_threshold: Number(c.min_data_threshold) || 0,
        insufficient_label: c.insufficient_label || "Insufficient data",
        components: c.components, score_labels: c.score_labels, flag_rules: c.flag_rules,
      };
    }
    if (!advanced && !weightOk && !window.confirm(`Top-level weights total ${Math.round(total)}%, not 100%. Save anyway?`)) return;
    setSaving(true);
    try { await api.put(`/scoring/configs/${config.id}`, payload); toast.success("Saved as new version"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const act = async (verb) => {
    setBusy(true);
    try {
      if (verb === "toggle") { await api.post(`/scoring/configs/${config.id}/toggle`); toast.success(c.is_active ? "Deactivated" : "Activated"); }
      if (verb === "duplicate") { await api.post(`/scoring/configs/${config.id}/duplicate`); toast.success("Duplicated"); }
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid={`scoring-editor-${c.key}`}>
      {/* Header */}
      <div className={card}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-sora text-lg font-bold text-navy">{c.name}</h2>
              <span className="rounded-full bg-ink/10 px-2 py-0.5 text-[10px] font-semibold text-ink/50">v{c.version}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${c.is_active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-ink/15 bg-ink/5 text-ink/50"}`}>{c.is_active ? "Active" : "Inactive"}</span>
            </div>
            {c.purpose && <p className="mt-1 max-w-2xl text-sm text-ink/60">{c.purpose}</p>}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => act("toggle")} disabled={busy} data-testid="editor-toggle" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-ink/70 hover:border-loop hover:text-loop disabled:opacity-50"><Power className="h-3.5 w-3.5" />{c.is_active ? "Deactivate" : "Activate"}</button>
            <button onClick={() => act("duplicate")} disabled={busy} className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-semibold text-ink/70 hover:border-loop hover:text-loop disabled:opacity-50"><Copy className="h-3.5 w-3.5" />Duplicate</button>
            <button onClick={() => { setAdvanced((a) => !a); setJson(JSON.stringify(c, null, 2)); }} data-testid="editor-advanced" className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${advanced ? "border-loop bg-loop/5 text-loop" : "border-sky text-ink/70 hover:border-loop hover:text-loop"}`}><Code2 className="h-3.5 w-3.5" />Advanced</button>
          </div>
        </div>
        {(c.min_data_metric || c.min_data_threshold) && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-mist/40 px-3 py-2 text-xs text-ink/60">
            <Info className="h-3.5 w-3.5 text-loop" />Minimum data rule: if <b className="mx-1">{c.min_data_metric}</b> &lt; {c.min_data_threshold} → show "{c.insufficient_label}"
          </div>
        )}
      </div>

      {advanced ? (
        <div className={card}>
          <p className="mb-2 text-xs text-ink/50">Advanced JSON — full config edit. Save creates a new version.</p>
          <textarea data-testid="editor-json" className="h-[420px] w-full rounded-lg border border-sky bg-mist/20 p-3 font-mono text-xs text-navy outline-none focus:border-loop" value={json} onChange={(e) => setJson(e.target.value)} />
        </div>
      ) : (
        <>
          {/* Formula preview */}
          <div className={card}>
            <h3 className="mb-2 font-sora text-sm font-semibold text-navy">Formula</h3>
            <div data-testid="formula-preview" className="rounded-lg bg-navy/5 px-3 py-2.5 text-sm font-medium text-navy">
              {c.name} = {readableFormula(c.components) || "—"}
            </div>
          </div>

          {/* Components */}
          <div className={card}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-sora text-sm font-semibold text-navy">Components</h3>
              <span data-testid="weight-total" className={`rounded-full px-2.5 py-1 text-xs font-semibold ${weightOk ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>Total: {Math.round(total)}%</span>
            </div>
            <div className="space-y-2.5">
              {(c.components || []).map((comp, i) => (
                <ComponentCard key={comp.key} comp={comp} path={[i]} setWeight={setWeight} setBenchmark={setBenchmark} toggle={toggle} />
              ))}
            </div>
          </div>

          {/* Rating benchmarks */}
          <div className={card}>
            <h3 className="mb-1 font-sora text-sm font-semibold text-navy">Rating Benchmarks {c.label_kind === "capacity" && <span className="text-xs font-normal text-ink/50">(capacity status)</span>}</h3>
            <p className="mb-3 text-xs text-ink/50">Score ≥ threshold → label (top-down).</p>
            <div className="space-y-2">
              {(c.score_labels || []).map((sl, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-ink/50">≥</span>
                  <input type="number" data-testid={`label-min-${i}`} className="h-9 w-24 rounded-lg border border-sky px-2 text-sm" value={sl.min ?? 0} onChange={(e) => setLabel(i, "min", e.target.value)} />
                  <input className="h-9 flex-1 rounded-lg border border-sky px-2.5 text-sm" value={sl.label || ""} onChange={(e) => setLabel(i, "label", e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          {/* Flags generated */}
          <div className={card}>
            <h3 className="mb-1 font-sora text-sm font-semibold text-navy">Flags generated by this score</h3>
            <div className="mt-2 space-y-1.5">
              {(c.flag_rules || []).map((f) => (
                <div key={f.key} data-testid={`flag-${f.key}`} className="rounded-lg border border-sky/50 bg-mist/20 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${f.severity === "critical" ? "bg-red-500" : f.severity === "warning" ? "bg-amber-500" : f.severity === "positive" ? "bg-emerald-500" : "bg-sky-400"}`} />
                    <span className="text-sm font-semibold text-navy">{f.label}</span>
                    <code className="ml-auto rounded bg-white px-1.5 py-0.5 text-[10px] text-ink/50">{f.condition}</code>
                  </div>
                  {f.diagnosis && <p className="mt-1 text-xs text-ink/55">{f.diagnosis}</p>}
                  {f.recommended_action && <p className="mt-0.5 text-xs text-loop">→ {f.recommended_action}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Test calculation panel (Phase 2 — live) */}
          <TestPanel entityType={c.entity_type} />
        </>
      )}

      <div className="sticky bottom-0 flex justify-end border-t border-sky/50 bg-mist/80 py-3 backdrop-blur">
        <button onClick={save} disabled={saving} data-testid="editor-save" className="flex items-center gap-1.5 rounded-lg bg-loop px-5 py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save as new version
        </button>
      </div>
    </div>
  );
}
